import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { proyectosAPI, subdivisionesAPI } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Edit, Building2, Calendar, Shield, ChevronDown, ChevronRight, Layers, Upload, Download, Search, Loader2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import SubdivisionesManager from '../components/SubdivisionesManager';

const Proyectos = () => {
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProyectos, setExpandedProyectos] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [editingProyecto, setEditingProyecto] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    etapas: '',
    codigo_proyecto: '',
    tiene_subdivision: false,
    fecha_entrega: '',
    fecha_inicio_garantia_estructural: '',
    fecha_inicio_garantia_acabados: '',
    garantia_acabados_anios: 1,
    garantia_acabados_meses: 0,
    garantia_estructural_anios: 10,
    garantia_estructural_meses: 0,
  });
  const [saving, setSaving] = useState(false);
  const [importingSubdivisiones, setImportingSubdivisiones] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [subdivisionesMap, setSubdivisionesMap] = useState({});
  const searchTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadProyectos();
  }, []);

  const loadProyectos = async () => {
    try {
      const response = await proyectosAPI.getAll();
      const data = response.data || [];
      setProyectos(data);
      setLoading(false);

      // Load subdivisiones in background (non-blocking) for grouped dates
      const projectsWithSubs = data.filter(p => p.tiene_subdivision);
      if (projectsWithSubs.length > 0) {
        try {
          const results = await Promise.all(
            projectsWithSubs.map(p => subdivisionesAPI.getByProyecto(p.id))
          );
          const subMap = {};
          projectsWithSubs.forEach((p, idx) => {
            subMap[p.id] = results[idx].data || [];
          });
          setSubdivisionesMap(subMap);
        } catch (err) {
          console.error('Error loading subdivisiones for dates:', err);
        }
      }
    } catch (error) {
      toast.error('Error al cargar proyectos');
      setLoading(false);
    }
  };

  // Importar subdivisiones desde Excel
  const handleImportSubdivisiones = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportingSubdivisiones(true);
    try {
      const response = await subdivisionesAPI.import(file);
      const result = response.data;
      toast.success(
        `Importacion completada: ${result.agregadas} agregadas, ${result.actualizadas} actualizadas`,
        { duration: 5000 }
      );
      loadProyectos();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al importar subdivisiones';
      toast.error(errorMessage, { duration: 6000 });
    } finally {
      setImportingSubdivisiones(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Descargar plantilla de subdivisiones
  const handleDownloadPlantilla = async () => {
    try {
      const response = await subdivisionesAPI.downloadPlantilla();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_subdivisiones.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };

  const handleOpenCreate = () => {
    setEditingProyecto(null);
    setFormData({
      nombre: '',
      direccion: '',
      etapas: '',
      codigo_proyecto: '',
      tiene_subdivision: false,
      fecha_entrega: '',
      fecha_inicio_garantia_estructural: '',
      fecha_inicio_garantia_acabados: '',
      garantia_acabados_anios: 1,
      garantia_acabados_meses: 0,
      garantia_estructural_anios: 10,
      garantia_estructural_meses: 0,
    });
    setShowDialog(true);
  };

  const handleOpenEdit = (proyecto) => {
    setEditingProyecto(proyecto);
    setFormData({
      nombre: proyecto.nombre || '',
      direccion: proyecto.direccion || '',
      etapas: Array.isArray(proyecto.etapas) ? proyecto.etapas.join(', ') : '',
      codigo_proyecto: proyecto.codigo_proyecto || '',
      tiene_subdivision: proyecto.tiene_subdivision || false,
      fecha_entrega: proyecto.fecha_entrega || '',
      fecha_inicio_garantia_estructural: proyecto.fecha_inicio_garantia_estructural || '',
      fecha_inicio_garantia_acabados: proyecto.fecha_inicio_garantia_acabados || '',
      garantia_acabados_anios: proyecto.garantia_acabados_anios ?? 1,
      garantia_acabados_meses: proyecto.garantia_acabados_meses ?? 0,
      garantia_estructural_anios: proyecto.garantia_estructural_anios ?? 10,
      garantia_estructural_meses: proyecto.garantia_estructural_meses ?? 0,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre del proyecto es requerido');
      return;
    }

    setSaving(true);
    try {
      const etapasArray = formData.etapas
        .split(',')
        .map(e => e.trim())
        .filter(e => e !== '');

      const data = {
        nombre: formData.nombre,
        direccion: formData.direccion,
        etapas: etapasArray,
        codigo_proyecto: formData.codigo_proyecto || null,
        tiene_subdivision: formData.tiene_subdivision,
        fecha_entrega: formData.fecha_entrega || null,
        fecha_inicio_garantia_estructural: formData.tiene_subdivision ? null : (formData.fecha_inicio_garantia_estructural || null),
        fecha_inicio_garantia_acabados: formData.tiene_subdivision ? null : (formData.fecha_inicio_garantia_acabados || null),
        garantia_acabados_anios: formData.tiene_subdivision ? 0 : parseInt(formData.garantia_acabados_anios),
        garantia_acabados_meses: formData.tiene_subdivision ? 0 : parseInt(formData.garantia_acabados_meses),
        garantia_estructural_anios: formData.tiene_subdivision ? 0 : parseInt(formData.garantia_estructural_anios),
        garantia_estructural_meses: formData.tiene_subdivision ? 0 : parseInt(formData.garantia_estructural_meses),
      };

      if (editingProyecto) {
        await proyectosAPI.update(editingProyecto.id, data);
        toast.success('Proyecto actualizado correctamente');
      } else {
        await proyectosAPI.create(data);
        toast.success('Proyecto creado correctamente');
      }

      setShowDialog(false);
      loadProyectos();
    } catch (error) {
      toast.error('Error al guardar el proyecto');
    } finally {
      setSaving(false);
    }
  };

  const formatGarantia = (anios, meses) => {
    const parts = [];
    if (anios > 0) parts.push(`${anios} ano${anios > 1 ? 's' : ''}`);
    if (meses > 0) parts.push(`${meses} mes${meses > 1 ? 'es' : ''}`);
    return parts.length > 0 ? parts.join(' y ') : '0';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Build grouped delivery dates for projects with subdivisions
  // Groups subdivisions by date: "15/03/2026 (Sub A, Sub B). 20/04/2026 (Sub C)"
  const getGroupedFechasEntrega = (proyectoId) => {
    const subs = subdivisionesMap[proyectoId] || [];
    const dateMap = {};
    subs.filter(s => s.fecha_entrega).forEach(s => {
      if (!dateMap[s.fecha_entrega]) dateMap[s.fecha_entrega] = [];
      dateMap[s.fecha_entrega].push(s.nombre);
    });
    return Object.entries(dateMap).map(([fecha, nombres]) => ({
      fecha,
      nombres,
    }));
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.trim()) {
      setSearching(true);
      searchTimerRef.current = setTimeout(() => setSearching(false), 300);
    } else {
      setSearching(false);
    }
  };

  const filteredProyectos = useMemo(() => {
    if (!searchQuery.trim()) return proyectos;
    const q = searchQuery.toLowerCase();
    return proyectos.filter(p =>
      (p.nombre || '').toLowerCase().includes(q) ||
      (p.codigo_proyecto || '').toLowerCase().includes(q) ||
      (p.direccion || '').toLowerCase().includes(q)
    );
  }, [proyectos, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#9d8272] mx-auto"></div>
          <p className="mt-4 text-sm text-[#5A5A5A]">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Proyectos
          </h1>
          <p className="text-sm sm:text-lg text-[#5A5A5A] mt-1">Gestiona los proyectos y sus garantias</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadPlantilla}
            className="text-sm"
            data-testid="download-plantilla-subdivisiones"
          >
            <Download size={16} className="mr-2" />
            Plantilla
          </Button>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importingSubdivisiones}
            className="text-sm"
            data-testid="import-subdivisiones"
          >
            <Upload size={16} className="mr-2" />
            {importingSubdivisiones ? 'Importando...' : 'Importar Subdivisiones'}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImportSubdivisiones}
            accept=".xlsx,.xls"
            className="hidden"
          />
          <Button
            onClick={handleOpenCreate}
            className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
            data-testid="nuevo-proyecto-btn"
          >
            <Plus size={18} className="mr-2" />
            Nuevo Proyecto
          </Button>
        </div>
      </div>

      {/* Filtro de busqueda */}
      <div className="relative max-w-md" data-testid="search-proyectos">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {searching ? (
            <Loader2 size={16} className="text-[#9d8272] animate-spin" />
          ) : (
            <Search size={16} className="text-[#888888]" />
          )}
        </div>
        <Input
          type="text"
          placeholder="Buscar por codigo, nombre o direccion..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-9 border-[#E8E8E7] focus:border-[#9d8272]"
          data-testid="search-proyectos-input"
        />
      </div>

      {/* Lista de Proyectos */}
      <div className="grid gap-4">
        {filteredProyectos.map((proyecto) => {
          const groupedFechas = proyecto.tiene_subdivision ? getGroupedFechasEntrega(proyecto.id) : [];

          return (
            <Card key={proyecto.id} className="bg-white border border-[#E8E8E7] shadow-sm" data-testid={`proyecto-card-${proyecto.id}`}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 size={20} className="text-[#9d8272]" />
                      <h3 className="font-semibold text-lg text-[#2D2D2D]">{proyecto.nombre}</h3>
                      {proyecto.codigo_proyecto && (
                        <Badge variant="secondary" className="text-xs bg-[#E8F4E8] text-[#2D2D2D]">
                          {proyecto.codigo_proyecto}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-[#5A5A5A] mb-3">{proyecto.direccion || 'Sin direccion'}</p>

                    {/* Fecha de Entrega - proyecto sin subdivisiones */}
                    {!proyecto.tiene_subdivision && proyecto.fecha_entrega && (
                      <div className="flex items-center gap-2 mb-3 bg-blue-50 px-3 py-2 rounded-lg border border-blue-100" data-testid={`fecha-entrega-proyecto-${proyecto.id}`}>
                        <Calendar size={16} className="text-blue-600 flex-shrink-0" />
                        <span className="text-sm text-blue-800">
                          <span className="font-medium">Fecha de Entrega:</span> {formatDate(proyecto.fecha_entrega)}
                        </span>
                      </div>
                    )}

                    {/* Fechas de Entrega agrupadas - proyecto CON subdivisiones */}
                    {proyecto.tiene_subdivision && groupedFechas.length > 0 && (
                      <div className="flex items-start gap-2 mb-3 bg-blue-50 px-3 py-2 rounded-lg border border-blue-100" data-testid={`fechas-entrega-grouped-${proyecto.id}`}>
                        <Calendar size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-800">
                          <span className="font-medium">Fecha de Entrega: </span>
                          {groupedFechas.map((g, idx) => (
                            <span key={idx}>
                              {formatDate(g.fecha)} ({g.nombres.join(', ')})
                              {idx < groupedFechas.length - 1 ? '. ' : ''}
                            </span>
                          ))}
                        </p>
                      </div>
                    )}

                    {/* Garantias: solo si NO tiene subdivisiones */}
                    {!proyecto.tiene_subdivision && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[#F5F5F5] p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Shield size={16} className="text-[#8c926f]" />
                          <div>
                            <p className="text-xs text-[#888888]">Garantia Acabados</p>
                            <p className="text-sm font-medium text-[#2D2D2D]">
                              {formatGarantia(proyecto.garantia_acabados_anios ?? 1, proyecto.garantia_acabados_meses ?? 0)}
                            </p>
                            {proyecto.fecha_inicio_garantia_acabados && (
                              <p className="text-[10px] text-[#888888]">Desde: {proyecto.fecha_inicio_garantia_acabados}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Shield size={16} className="text-[#9d8272]" />
                          <div>
                            <p className="text-xs text-[#888888]">Garantia Estructural</p>
                            <p className="text-sm font-medium text-[#2D2D2D]">
                              {formatGarantia(proyecto.garantia_estructural_anios ?? 10, proyecto.garantia_estructural_meses ?? 0)}
                            </p>
                            {proyecto.fecha_inicio_garantia_estructural && (
                              <p className="text-[10px] text-[#888888]">Desde: {proyecto.fecha_inicio_garantia_estructural}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 w-[160px] flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenEdit(proyecto)}
                      className="border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white w-full"
                      data-testid={`editar-proyecto-${proyecto.id}`}
                    >
                      <Edit size={16} className="mr-1" />
                      Editar
                    </Button>
                    {proyecto.tiene_subdivision && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedProyectos(prev => ({ ...prev, [proyecto.id]: !prev[proyecto.id] }))}
                        className="text-[#5A5A5A]"
                        data-testid={`toggle-subdivisiones-${proyecto.id}`}
                      >
                        {expandedProyectos[proyecto.id] ? <ChevronDown size={16} className="mr-1" /> : <ChevronRight size={16} className="mr-1" />}
                        <Layers size={14} className="mr-1" />
                        Subdivisiones
                      </Button>
                    )}
                  </div>
                </div>

                {/* Subdivisiones expandibles */}
                {proyecto.tiene_subdivision && expandedProyectos[proyecto.id] && (
                  <div className="mt-4 pt-4 border-t border-[#E8E8E7]">
                    <SubdivisionesManager proyecto={proyecto} onUpdate={loadProyectos} />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {filteredProyectos.length === 0 && searchQuery && (
          <Card className="bg-white border border-[#E8E8E7]">
            <CardContent className="p-8 text-center">
              <Search size={48} className="mx-auto text-[#c5b8a2] mb-4" />
              <p className="text-[#5A5A5A]">No se encontraron proyectos para "{searchQuery}"</p>
            </CardContent>
          </Card>
        )}

        {proyectos.length === 0 && !searchQuery && (
          <Card className="bg-white border border-[#E8E8E7]">
            <CardContent className="p-8 text-center">
              <Building2 size={48} className="mx-auto text-[#c5b8a2] mb-4" />
              <p className="text-[#5A5A5A]">No hay proyectos registrados</p>
              <Button
                onClick={handleOpenCreate}
                className="mt-4 bg-[#9d8272] hover:bg-[#8a7162] text-white"
              >
                Crear primer proyecto
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog para Crear/Editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#2D2D2D]">
              {editingProyecto ? 'Editar Proyecto' : 'Nuevo Proyecto'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Datos basicos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-sm">Nombre del Proyecto *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="border-[#c5b8a2] mt-1"
                  placeholder="Ej: Residencial Los Pinos"
                  data-testid="proyecto-nombre-input"
                />
              </div>

              <div>
                <Label className="text-sm">Codigo de Proyecto</Label>
                <Input
                  value={formData.codigo_proyecto}
                  onChange={(e) => setFormData({ ...formData, codigo_proyecto: e.target.value.slice(0, 20) })}
                  className="border-[#c5b8a2] mt-1"
                  placeholder="Ej: PROY-001"
                  maxLength={20}
                  data-testid="proyecto-codigo-input"
                />
              </div>

              <div>
                <Label className="text-sm">Direccion</Label>
                <Input
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  className="border-[#c5b8a2] mt-1"
                  placeholder="Ej: Av. Principal 123"
                  data-testid="proyecto-direccion-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm">Etapas (separadas por coma)</Label>
              <Input
                value={formData.etapas}
                onChange={(e) => setFormData({ ...formData, etapas: e.target.value })}
                className="border-[#c5b8a2] mt-1"
                placeholder="Ej: Etapa 1, Etapa 2, Etapa 3"
                data-testid="proyecto-etapas-input"
              />
            </div>

            {/* Checkbox tiene subdivision */}
            <div className="flex items-center space-x-2 p-3 bg-[#F5F5F5] rounded-lg">
              <Checkbox
                id="tiene_subdivision"
                checked={formData.tiene_subdivision}
                onCheckedChange={(checked) => setFormData({ ...formData, tiene_subdivision: checked })}
                data-testid="tiene-subdivision-checkbox"
              />
              <Label htmlFor="tiene_subdivision" className="text-sm cursor-pointer">
                El proyecto tiene subdivisiones
              </Label>
            </div>

            {formData.tiene_subdivision && (
              <p className="text-xs text-[#888888] bg-yellow-50 p-2 rounded border border-yellow-200">
                Las garantias se configuraran a nivel de subdivision
              </p>
            )}

            {/* Fecha de Entrega */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <Label className="text-sm font-semibold text-blue-800 flex items-center gap-2 mb-2">
                <Calendar size={16} />
                Fecha de Entrega
              </Label>
              <p className="text-xs text-blue-600 mb-2">Campo opcional</p>
              <Input
                type="date"
                value={formData.fecha_entrega}
                onChange={(e) => setFormData({ ...formData, fecha_entrega: e.target.value })}
                className="border-blue-200 bg-white h-10"
                data-testid="proyecto-fecha-entrega-input"
              />
            </div>

            {/* Garantia de Acabados */}
            <div className={`bg-[#F5F5F5] p-4 rounded-lg ${formData.tiene_subdivision ? 'opacity-50' : ''}`}>
              <Label className="text-sm font-semibold text-[#8c926f] flex items-center gap-2 mb-3">
                <Shield size={16} />
                Garantia de Acabados
              </Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-[#888888]">Anos (0-10)</Label>
                  <Select
                    value={String(formData.garantia_acabados_anios)}
                    onValueChange={(v) => setFormData({ ...formData, garantia_acabados_anios: parseInt(v) })}
                    disabled={formData.tiene_subdivision}
                  >
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(11)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i} ano{i !== 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#888888]">Meses (0-11)</Label>
                  <Select
                    value={String(formData.garantia_acabados_meses)}
                    onValueChange={(v) => setFormData({ ...formData, garantia_acabados_meses: parseInt(v) })}
                    disabled={formData.tiene_subdivision}
                  >
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i} mes{i !== 1 ? 'es' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#888888]">Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio_garantia_acabados}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio_garantia_acabados: e.target.value })}
                    className="border-[#c5b8a2] mt-1 h-10"
                    disabled={formData.tiene_subdivision}
                  />
                </div>
              </div>
            </div>

            {/* Garantia Estructural */}
            <div className={`bg-[#F5F5F5] p-4 rounded-lg ${formData.tiene_subdivision ? 'opacity-50' : ''}`}>
              <Label className="text-sm font-semibold text-[#9d8272] flex items-center gap-2 mb-3">
                <Shield size={16} />
                Garantia Estructural
              </Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-[#888888]">Anos (0-10)</Label>
                  <Select
                    value={String(formData.garantia_estructural_anios)}
                    onValueChange={(v) => setFormData({ ...formData, garantia_estructural_anios: parseInt(v) })}
                    disabled={formData.tiene_subdivision}
                  >
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(11)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i} ano{i !== 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#888888]">Meses (0-11)</Label>
                  <Select
                    value={String(formData.garantia_estructural_meses)}
                    onValueChange={(v) => setFormData({ ...formData, garantia_estructural_meses: parseInt(v) })}
                    disabled={formData.tiene_subdivision}
                  >
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i} mes{i !== 1 ? 'es' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#888888]">Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio_garantia_estructural}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio_garantia_estructural: e.target.value })}
                    className="border-[#c5b8a2] mt-1 h-10"
                    disabled={formData.tiene_subdivision}
                  />
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#E8E8E7]">
              <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="proyecto-cancelar-btn">
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
                data-testid="proyecto-guardar-btn"
              >
                {saving ? 'Guardando...' : editingProyecto ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Proyectos;
